import { BaseParser } from '../BaseParser';

/**
 * Bank of America Parser
 * Handles BofA checking account CSV exports with summary section
 */
export class BofAParser extends BaseParser {
  constructor() {
    super();
    this.name = 'BofAParser';
    this.priority = 10;
  }

  /**
   * Detect if this is a BofA CSV file
   */
  detect(header: string, bank: string): number {
    let confidence = 0;

    // Bank hint from folder structure
    if (bank === 'bofa' || bank === 'bank of america' || bank === 'bankofamerica') {
      confidence += 50;
    }

    // BofA format has a distinctive summary section at top
    // First line: "Description,,Summary Amt."
    // Or contains "Beginning balance as of"
    if (
      header.includes('summary amt') ||
      header.includes('beginning balance as of') ||
      header.includes('total credits') ||
      header.includes('total debits')
    ) {
      confidence += 50;
    }

    return confidence;
  }

  /**
   * Parse BofA CSV
   * Format has summary section (lines 1-5), then:
   * Date, Description, Amount, Running Bal.
   *
   * Date format: MM/DD/YYYY
   * Amount: "2,000.00" with quotes and commas, negative = debit
   */
  parse(lines: string[], filename: string): any[] {
    const transactions = [];

    // Find the data header line (contains "Date,Description,Amount")
    let dataStartIndex = -1;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].toLowerCase();
      if (line.includes('date') && line.includes('description') && line.includes('amount')) {
        dataStartIndex = i + 1; // Start after header
        break;
      }
    }

    if (dataStartIndex === -1) {
      this.log('Could not find data header in BofA CSV', 'error');
      return [];
    }

    // Parse transactions starting after the header
    for (let i = dataStartIndex; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      try {
        const row = this.parseCSVLine(line);
        if (row.length < 3) continue;

        const [date, description, amount, runningBal] = row;

        // Skip "Beginning balance" rows
        if (description && description.toLowerCase().includes('beginning balance')) {
          continue;
        }

        // Skip if no date or description or amount
        if (!date || !description || !amount) continue;

        // Parse amount (remove commas and quotes)
        const amountStr = amount.replace(/,/g, '').replace(/"/g, '');
        const amountValue = this.parseAmount(amountStr);

        // Determine transaction type
        const transactionType = this.inferTransactionType(description, amountValue);

        transactions.push(
          this.createTransaction({
            date: this.parseDate(date),
            postDate: this.parseDate(date),
            description: description || 'Unknown',
            amount: amountValue,
            type: transactionType,
            category: 'Uncategorized',
            memo: runningBal ? `Balance: ${runningBal}` : '',
            source: filename || 'BofA',
            bank: 'bofa',
            balance: runningBal ? this.parseAmount(runningBal.replace(/,/g, '')) : undefined,
            rawData: row,
          })
        );
      } catch (error: any) {
        this.log(`Error parsing line ${i}: ${error.message}`, 'error');
      }
    }

    return transactions;
  }

  /**
   * Infer transaction type based on description and amount
   */
  inferTransactionType(description: string, amount: number): string {
    const desc = description.toLowerCase();

    // Check for common transaction types
    if (desc.includes('payroll') || desc.includes('salary')) {
      return 'Deposit';
    }

    if (desc.includes('zelle payment')) {
      return 'Transfer';
    }

    if (desc.includes('transfer')) {
      return 'Transfer';
    }

    if (desc.includes('withdrawal') || desc.includes('atm')) {
      return 'Withdrawal';
    }

    if (desc.includes('fee') || desc.includes('charge')) {
      return 'Fee';
    }

    if (desc.includes('interest')) {
      return 'Interest';
    }

    if (desc.includes('deposit')) {
      return 'Deposit';
    }

    if (desc.includes('check')) {
      return 'Check';
    }

    if (desc.includes('debit card') || desc.includes('pos')) {
      return 'Debit';
    }

    // Default based on amount sign
    return amount >= 0 ? 'Credit' : 'Debit';
  }

  getInfo() {
    return {
      name: this.name,
      priority: this.priority,
      supportedBanks: ['bofa', 'bank of america', 'bankofamerica'],
      formats: ['BofA Checking Account with Summary'],
      dateFormat: 'MM/DD/YYYY',
      notes: 'Handles BofA CSV exports with summary section at top',
    };
  }
}
